// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AllRequestsList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, getStatusBadgeColor, trainingAPI } from '../../services/api';
import './AllRequestsList.css';

// SVG Icons
const Icons = {
  Close: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="arl-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Service: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
};

const AllRequestsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [startingTraining, setStartingTraining] = useState<number | null>(null);
  const [showTechModal, setShowTechModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(true);
  const [existingEnrollments, setExistingEnrollments] = useState<Set<string>>(new Set());
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await adminAPI.getAllRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    if (updatingStatus === id) return;
    
    const confirmMessage = `Are you sure you want to change this request status to ${status.toUpperCase()}?`;
    if (!window.confirm(confirmMessage)) return;
    
    setUpdatingStatus(id);
    
    try {
      await adminAPI.updateRequestStatus(id, status);
      alert(`✅ Request status updated to ${status.toUpperCase()} successfully`);
      await fetchRequests();
    } catch (error: any) {
      console.error('Error updating status:', error);
      const errorMsg = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.message || 
                       'Failed to update status';
      alert(`❌ Failed to update status: ${errorMsg}`);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const checkExistingEnrollments = async (userId: number, technologies: string[]) => {
    try {
      const response = await adminAPI.getUserEnrollments(userId);
      const existingTechs = new Set<string>(response.data.map((e: any) => e.technology as string));
      setExistingEnrollments(existingTechs);

      const notStartedTechs = technologies.filter(tech => !existingTechs.has(tech));
      setSelectedTechnologies(notStartedTechs);
      setSelectAll(notStartedTechs.length === technologies.length);
    } catch (error) {
      console.error('Error checking enrollments:', error);
      setSelectedTechnologies([...technologies]);
    }
  };

  const openTechSelectionModal = (request: any) => {
    setSelectedRequest(request);
    checkExistingEnrollments(request.user, request.selected_technologies);
    setShowTechModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeTechModal = () => {
    setShowTechModal(false);
    setSelectedRequest(null);
    setSelectedTechnologies([]);
    setExistingEnrollments(new Set());
    document.body.style.overflow = 'auto';
  };

  const toggleTechnology = (tech: string) => {
    setSelectedTechnologies(prev => 
      prev.includes(tech) 
        ? prev.filter(t => t !== tech)
        : [...prev, tech]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedTechnologies([]);
      setSelectAll(false);
    } else {
      const notStartedTechs = selectedRequest?.selected_technologies.filter(
        (tech: string) => !existingEnrollments.has(tech)
      ) || [];
      setSelectedTechnologies(notStartedTechs);
      setSelectAll(true);
    }
  };

  const startTraining = async () => {
    if (!selectedRequest) return;

    if (selectedTechnologies.length === 0) {
      alert('Please select at least one technology to start training.');
      return;
    }

    setStartingTraining(selectedRequest.id);
    closeTechModal();

    try {
      await trainingAPI.startTraining(selectedRequest.id, selectedTechnologies);
      alert(`Training started successfully!\n\nEnrollments created for: ${selectedTechnologies.join(', ')}`);
      fetchRequests();
    } catch (error: any) {
      console.error('Error starting training:', error);
      const errorMsg = error.response?.data?.error || 'Failed to start training. Make sure the request is approved.';
      alert(errorMsg);
    } finally {
      setStartingTraining(null);
    }
  };

  const getServiceDisplayName = (request: any): string => {
    if (request.service_name_display) {
      return request.service_name_display;
    }
    if (request.service_name) {
      return request.service_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    }
    if (request.service && typeof request.service === 'object') {
      return request.service.name?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown Service';
    }
    if (request.service && typeof request.service === 'string') {
      return request.service.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    }
    return 'Unknown Service';
  };

  const isTrainingRequest = (request: any): boolean => {
    const serviceName = getServiceDisplayName(request);
    return serviceName === 'Training Programs' || request.service_name === 'training_programs';
  };

  const canStartTraining = (request: any): boolean => {
    return isTrainingRequest(request) && 
           request.status === 'approved' && 
           request.selected_technologies && 
           request.selected_technologies.length > 0;
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'pending': return '#f59e0b';
      case 'in_review': return '#0ea5e9';
      case 'approved': return '#10b981';
      case 'in_progress': return '#8b5cf6';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusBackground = (status: string) => {
    switch(status) {
      case 'pending': return '#fef3c7';
      case 'in_review': return '#dbeafe';
      case 'approved': return '#d1fae5';
      case 'in_progress': return '#ede9fe';
      case 'completed': return '#d1fae5';
      case 'cancelled': return '#fee2e2';
      default: return '#f3f4f6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'pending': return 'Pending';
      case 'in_review': return 'In Review';
      case 'approved': return 'Approved';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress' || r.status === 'in_review').length,
    completed: requests.filter(r => r.status === 'completed').length,
    approved: requests.filter(r => r.status === 'approved').length,
  };

  if (loading) {
    return (
      <div className="arl-loading-container">
        <div className="arl-spinner">
          <Icons.Spinner />
        </div>
        <p className="arl-loading-text">Loading requests...</p>
      </div>
    );
  }

  return (
    <div className="arl-container">
      {/* Technology Selection Modal */}
      {showTechModal && selectedRequest && (
        <div className="arl-modal-overlay" onClick={closeTechModal}>
          <div className="arl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="arl-modal-header">
              <h3 className="arl-modal-title">Select Technologies to Start Training</h3>
              <button className="arl-modal-close" onClick={closeTechModal}>
                <Icons.Close />
              </button>
            </div>
            <div className="arl-modal-body">
              <p className="arl-modal-user">
                Starting training for: <strong>{selectedRequest.user_name}</strong>
              </p>
              
              {existingEnrollments.size > 0 && (
                <div className="arl-already-started">
                  <Icons.Check />
                  <span>Already in training: <strong>{Array.from(existingEnrollments).join(', ')}</strong></span>
                </div>
              )}
              
              <div className="arl-select-all">
                <label className="arl-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    disabled={selectedRequest.selected_technologies.filter((t: string) => !existingEnrollments.has(t)).length === 0}
                    className="arl-checkbox"
                  />
                  <span>Select All Technologies</span>
                </label>
              </div>
              
              <div className="arl-tech-list">
                {selectedRequest.selected_technologies.map((tech: string) => {
                  const isAlreadyStarted = existingEnrollments.has(tech);
                  const isSelected = selectedTechnologies.includes(tech);
                  
                  return (
                    <label 
                      key={tech} 
                      className={`arl-tech-item ${isAlreadyStarted ? 'arl-tech-started' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTechnology(tech)}
                        disabled={isAlreadyStarted}
                        className="arl-checkbox"
                      />
                      <span className="arl-tech-name">{tech}</span>
                      {isAlreadyStarted && (
                        <span className="arl-tech-badge">Already in training</span>
                      )}
                    </label>
                  );
                })}
              </div>
              
              {selectedTechnologies.length === 0 && (
                <p className="arl-tech-warning">
                  {existingEnrollments.size === selectedRequest.selected_technologies.length
                    ? "All technologies are already in training for this user"
                    : "Please select at least one technology to start training"}
                </p>
              )}
            </div>
            <div className="arl-modal-footer">
              <button className="arl-modal-cancel" onClick={closeTechModal}>
                Cancel
              </button>
              <button 
                className="arl-modal-start"
                onClick={startTraining}
                disabled={selectedTechnologies.length === 0 || startingTraining === selectedRequest.id}
              >
                {startingTraining === selectedRequest.id ? 'Starting...' : `Start Training (${selectedTechnologies.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="arl-header">
        <div className="arl-header-left">
          <h1 className="arl-title">Service Requests</h1>
          <p className="arl-subtitle">Manage and track all service requests from users</p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="arl-stats-bar">
        <div className="arl-stat-item">
          <span className="arl-stat-label">Total Requests</span>
          <span className="arl-stat-value">{counts.all}</span>
        </div>
        <div className="arl-stat-divider"></div>
        <div className="arl-stat-item">
          <span className="arl-stat-label">Pending</span>
          <span className="arl-stat-value arl-stat-pending">{counts.pending}</span>
        </div>
        <div className="arl-stat-divider"></div>
        <div className="arl-stat-item">
          <span className="arl-stat-label">In Progress</span>
          <span className="arl-stat-value arl-stat-progress">{counts.in_progress}</span>
        </div>
        <div className="arl-stat-divider"></div>
        <div className="arl-stat-item">
          <span className="arl-stat-label">Approved</span>
          <span className="arl-stat-value arl-stat-approved">{counts.approved}</span>
        </div>
        <div className="arl-stat-divider"></div>
        <div className="arl-stat-item">
          <span className="arl-stat-label">Completed</span>
          <span className="arl-stat-value arl-stat-completed">{counts.completed}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="arl-filter-bar">
        <button 
          className={`arl-filter-btn ${filter === 'all' ? 'arl-filter-active' : ''}`} 
          onClick={() => setFilter('all')}
        >
          All
          <span className="arl-filter-count">{counts.all}</span>
        </button>
        <button 
          className={`arl-filter-btn ${filter === 'pending' ? 'arl-filter-active' : ''}`} 
          onClick={() => setFilter('pending')}
        >
          Pending
          <span className="arl-filter-count">{counts.pending}</span>
        </button>
        <button 
          className={`arl-filter-btn ${filter === 'in_progress' ? 'arl-filter-active' : ''}`} 
          onClick={() => setFilter('in_progress')}
        >
          In Progress
          <span className="arl-filter-count">{counts.in_progress}</span>
        </button>
        <button 
          className={`arl-filter-btn ${filter === 'approved' ? 'arl-filter-active' : ''}`} 
          onClick={() => setFilter('approved')}
        >
          Approved
          <span className="arl-filter-count">{counts.approved}</span>
        </button>
        <button 
          className={`arl-filter-btn ${filter === 'completed' ? 'arl-filter-active' : ''}`} 
          onClick={() => setFilter('completed')}
        >
          Completed
          <span className="arl-filter-count">{counts.completed}</span>
        </button>
      </div>

      {/* Table */}
      <div className="arl-table-wrapper">
        <table className="arl-table">
          <thead>
            <tr>
              <th className="arl-col-id">ID</th>
              <th className="arl-col-user">User</th>
              <th className="arl-col-service">Service</th>
              <th className="arl-col-tech">Technologies</th>
              <th className="arl-col-status">Status</th>
              <th className="arl-col-date">Submitted</th>
              <th className="arl-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan={7} className="arl-empty-state">
                  <Icons.Empty />
                  <p className="arl-empty-text">No requests found</p>
                  <p className="arl-empty-subtext">Service requests will appear here once users submit them</p>
                </td>
              </tr>
            ) : (
              filteredRequests.map((request) => (
                <tr key={request.id} className="arl-table-row">
                  <td className="arl-col-id">
                    <span className="arl-id-badge">#{request.id}</span>
                  </td>
                  <td className="arl-col-user">
                    <span className="arl-user-name">
                      <Icons.User />
                      {request.user_name || request.user_email}
                    </span>
                  </td>
                  <td className="arl-col-service">
                    <span className="arl-service-name">
                      <Icons.Service />
                      {getServiceDisplayName(request)}
                    </span>
                  </td>
                  <td className="arl-col-tech">
                    {request.selected_technologies && request.selected_technologies.length > 0 ? (
                      <div className="arl-tech-list-small">
                        {request.selected_technologies.slice(0, 3).map((tech: string, idx: number) => (
                          <span key={idx} className="arl-tech-badge-small">{tech}</span>
                        ))}
                        {request.selected_technologies.length > 3 && (
                          <span className="arl-tech-badge-small">+{request.selected_technologies.length - 3}</span>
                        )}
                      </div>
                    ) : (
                      <span className="arl-no-tech">—</span>
                    )}
                  </td>
                  <td className="arl-col-status">
                    <span 
                      className="arl-status-badge"
                      style={{
                        background: getStatusBackground(request.status),
                        color: getStatusColor(request.status)
                      }}
                    >
                      <Icons.StatusDot />
                      {getStatusLabel(request.status)}
                    </span>
                  </td>
                  <td className="arl-col-date">
                    <span className="arl-date-text">
                      <Icons.Calendar />
                      {new Date(request.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="arl-col-actions">
                    <div className="arl-actions">
                      <button
                        className="arl-action-btn arl-action-view"
                        onClick={() => navigate(`/dashboard/admin/requests/${request.id}`)}
                        title="View Request"
                      >
                        <Icons.View />
                      </button>
                      {canStartTraining(request) && (
                        <button
                          className="arl-action-btn arl-action-start"
                          onClick={() => openTechSelectionModal(request)}
                          disabled={startingTraining === request.id}
                          title="Start Training"
                        >
                          {startingTraining === request.id ? (
                            <span className="arl-spinner-small"></span>
                          ) : (
                            'Start Training'
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllRequestsList;